/*
 * Application loader logic
 *
 * Setup vue plugins and compoenents before loading App.
 */

import Vue from "vue";

Vue.config.productionTip = false;
Vue.config.autoAddKeyAttributes = true;  // see insight-translate module

import {provider as provider_router} from "./router.js";
Vue.use(provider_router);

//------------------------------------------------------------
// Import source map resolver utility
import { resolveStackTraceEnhanced } from "./utils/sourceMapResolver.js";

// Custom error handler to send errors to Django endpoint
async function sendErrorToServer(error, errorInfo = {}) {
  try {
    // Resolve stack trace using source maps
    let resolvedStack = error.stack || '';
    if (error.stack) {
      try {
        resolvedStack = await resolveStackTraceEnhanced(error.stack);
      } catch (resolveError) {
        // If source map resolution fails, use original stack
        console.debug('Source map resolution failed, using original stack:', resolveError);
      }
    }
    
    const errorData = {
      message: error.message || String(error),
      stack: resolvedStack,
      originalStack: error.stack || '', // Keep original for reference
      name: error.name || 'Error',
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      ...errorInfo
    };

    // Add user info if available
    if (window.epr_config) {
      errorData.user_id = window.epr_config.insightuser_id;
      errorData.username = window.epr_config.username;
    }

    // Send error to Django endpoint
    const apiBase = window.DISPATCHER_URL || '/insight/';
    const errorEndpoint = apiBase + 'error_logger/';
    
    // Use fetch to avoid circular dependencies with Vue.http
    fetch(errorEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(errorData),
      credentials: 'same-origin'
    }).catch(err => {
      // Silently fail if error reporting itself fails
      console.error('Failed to send error to server:', err);
    });
  } catch (err) {
    // Silently fail if error reporting itself fails
    console.error('Error in error handler:', err);
  }
}

// Set up Vue error handler
Vue.config.errorHandler = function(err, vm, info) {
  sendErrorToServer(err, {
    vueInfo: info,
    componentName: vm?.$options?.name || vm?.$options?.__name || 'Unknown',
  });
  console.error('Vue Error:', err, info);
};

// Set up global error handler for unhandled errors
window.addEventListener('error', function(event) {
  sendErrorToServer(event.error || new Error(event.message), {
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
    type: 'unhandled_error'
  });
});

// Set up unhandled promise rejection handler
window.addEventListener('unhandledrejection', function(event) {
  sendErrorToServer(event.reason || new Error('Unhandled Promise Rejection'), {
    type: 'unhandled_promise_rejection',
    promise: event.promise?.toString() || 'Unknown'
  });
});

import { provider as provider_api } from "@openapp/insight-api";
import {provider as provider_ui} from "@openapp/insight-ui";
import {provider as provider_pageset} from "@openapp/insight-pageset";
import {provider as provider_dashboard} from "@openapp/insight-dashboard";
import {provider as provider_components} from "@openapp/components";
import {provider as provider_edc} from "@openapp/insight-edc";
import {provider as provider_app} from "@openapp/insight-app";
import {provider as provider_outcomechart} from "@openapp/insight-outcomechart";
import {provider as provider_custom} from "@openapp/insight-custom";
import {provider as provider_forms} from "@openapp/forms";

Vue.use(provider_api);
Vue.use(provider_ui);
Vue.use(provider_pageset);
Vue.use(provider_dashboard);
Vue.use(provider_components);
Vue.use(provider_edc);
Vue.use(provider_app);
Vue.use(provider_outcomechart);
Vue.use(provider_custom);
Vue.use(provider_forms);

//------------------------------------------------------------
import App from "./App.vue";

new Vue({
  el: "#app",
  router: provider_router.router,
  apolloProvider: provider_api.apolloProvider,
  data: function () {
    return {
      endpoints: provider_api.endpoints,
      set_router_hook: (fn) => {
        provider_router.router_hooks.handler = fn;
      },
    };
  },
  created: function() {
    this.$ui.set_default_target(this);

    // I want to give the query builder access to the API service
    this.$qb.set_epr_api(this.$insight_epr_api);

  },
  render: (h) => h(App),
});

